<template>
  <div class="flex flex-col gap-1">
    <div class="flex flex-col">
      <div class="flex flex-row items-center px-1 md:px-0 gap-2 mb-2 md:mb-3">
        <Icon name="chevron-down" class="fg-quaternary" />
        <span class="font-semibold text-primary text-style-xl md:text-style-sm">
          {{ $t("components.parts.linked_to") }}
        </span>
      </div>

      <div class="flex flex-col gap-1">
        <div
          class="flex items-center gap-3 px-3 py-1.5 bg-primary border border-secondary rounded-md max-w-full h-12 md:h-10"
        >
          <Indicator
            :variant="'building'"
            :isGroup="false"
            mode="transparent"
            size="xs"
            :imageUrl="file.tenant.logo_url"
          />
          <div
            class="flex-1 text-primary text-style-base md:text-style-sm font-medium truncate"
          >
            {{ file.tenant.name }}
          </div>
        </div>
      </div>
      <div class="flex flex-col">
        <div
          v-for="(item, index) in file.spaces"
          :key="index"
          class="flex flex-col relative max-w-full ltr:pl-2 rtl:pr-2"
        >
          <div class="relative">
            <div
              class="absolute -top-0 ltr:left-0 rtl:right-0 w-4 border-primary ltr:border-l rtl:border-r"
              :class="last(file.spaces) == item ? 'h-6 md:h-5' : 'h-full'"
            />
            <div class="flex items-center relative max-w-full pt-1">
              <div
                class="w-2 h-2 ltr:rounded-bl-xs rtl:rounded-br-xs ltr:border-l rtl:border-r border-b border-primary absolute top-[18px] ltr:left-0 rtl:right-0"
              />
              <div class="flex-grow max-w-full ltr:pl-2 rtl:pr-2">
                <div
                  class="flex items-center gap-3 px-3 py-1.5 bg-primary border border-secondary rounded-md w-full h-12 md:h-10 truncate"
                >
                  <Indicator
                    :variant="formatIndicatorVariant(item.type.value)"
                    :isGroup="false"
                    mode="transparent"
                    size="xs"
                    :imageUrl="item.thumbnailUrl"
                  />
                  <div
                    class="flex-1 shrink-0 flex items-center gap-3 max-w-[calc(100%-80px)] text-primary text-style-base md:text-style-sm font-medium truncate"
                  >
                    <div class="truncate">
                      {{ item.name }}
                    </div>
                    <div v-if="item.identifier" class="max-w-24">
                      <Tooltip :title="item.identifier" :delay="300">
                        <Id
                          :size="maxTablet ? 'md' : 'sm'"
                          :label="item.identifier"
                          class="group-data-[active=true]:bg-brand-primary group-data-[active=true]:border-transparent group-data-[active=true]:[&>span]:!text-brand-secondary group-data-[active=true]:text-brand-secondary shrink-0"
                        />
                      </Tooltip>
                    </div>
                  </div>
                  <IconButton
                    v-if="!readonly"
                    variant="tertiary"
                    type="gray"
                    class="border-0 shrink-0"
                    size="2xs"
                    @click="handleRemoveLink(item)"
                  >
                    <Icon name="delete" size="full" />
                  </IconButton>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <LinkedToSelectModal
      v-if="!readonly"
      :title="$t('components.file_details.linked_to.title')"
      :description="$t('components.file_details.linked_to.description')"
      :buildingId="file.tenant.id"
      :initialState="{ buildings: [file.tenant.id], children: spaceIds }"
      :multiple="true"
      class="ltr:ml-auto rtl:mr-auto"
      @update="handleUpdateLinks"
    >
      <Button variant="secondary" type="gray" :size="maxTablet ? 'xl' : '2xs'">
        <template #start>
          <Icon name="plus" size="sm" />
        </template>
        {{ $t("components.buttons.add_link") }}
      </Button>
    </LinkedToSelectModal>
  </div>
</template>

<script setup>
// Import Vue Utilities
import { inject, ref } from "vue";

// Import Utilities
import route from "ziggy-js";
import { useI18n } from "vue-i18n";
import { router } from "@inertiajs/vue3";
import { remove, last, uniq } from "lodash";
import axios from "axios";
import { Tooltip } from "@/SharedComponents/Tooltips";

// Import Composables
import { useBreakpoints } from "@/Composables/useBreakpoints";

// Import Components
import { Icon } from "@/SharedComponents/Icon";
import { toast } from "@/SharedComponents/Toaster";
import { Indicator } from "@/SharedComponents/Indicator";
import { Button, IconButton } from "@/SharedComponents/Buttons";
import { LinkedToSelectModal } from "@/SharedComponents/Modals";
import { Id } from "@/SharedComponents/Badges";

const props = defineProps({
  file: { type: Object, required: true },
  readonly: { type: Boolean, default: false },
});

// Emitter
const emitter = inject("emitter");

// Translations
const { t } = useI18n();

// Breakpoints
const { maxTablet } = useBreakpoints();

// Refs
const spaceIds = ref(props.file.spaces.map((space) => space.id));

// Functions
const formatIndicatorVariant = (type) => {
  switch (type) {
    case "asset":
      return "asset";
    case "asset_group":
      return "asset_group";
    default:
      return "space";
  }
};

const handleUpdate = () => {
  axios
    .post(route("api.files.linked_to"), {
      note_ids: [props.file.id],
      tenant_id: props.file.tenant.id,
      space_ids: uniq(spaceIds.value),
    })
    .then((response) => {
      // Show success toasg
      toast.success(t("toasts.files.update.success"));

      // Update the files data
      emitter.emit("update-files-data", { data: response.data.data });

      // Reload the router
      router.reload();
    })
    .catch((error) => {
      if (error.response.status == 403) {
        toast.error(t("toasts.files.update.403"));
      } else {
        toast.error(
          error.response.data.message || t("toasts.files.update.error")
        );
      }
    });
};

const handleUpdateLinks = (value) => {
  spaceIds.value = value.children;

  handleUpdate();
};

const handleRemoveLink = (item) => {
  remove(spaceIds.value, (space) => space === item.id);

  handleUpdate();
};
</script>
